import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { QuestionCircleOutlined, RightOutlined, WarningOutlined } from '@ant-design/icons';

import { Badge } from '../../../../components/ui';
import { useDuongDan } from '../../../../hooks/useDuongDan';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import { CHANG_DOC_THANH_TIENG } from '../../nghiep-vu/hoi';
import type { AskResult } from '../../types';
import type { AskError, Turn } from '../../types-hoi';
import { LoiVan } from '../chung/LoiVan';
import { duongKhaiCot, TienTrinh } from '../chung/TienTrinh';
import { CauHoiTiep } from './CauHoiTiep';
import { KhoiSoLieu } from './KhoiSoLieu';
import { TienDoKichBan } from './TienDoKichBan';

/** Bong bóng câu hỏi của người dùng — căn phải, đọc như một lượt trong hội thoại. */
export const BONG_HOI =
  'max-w-[80%] whitespace-pre-wrap break-words rounded-lg bg-primary-50 px-4 py-2.5 text-sm font-medium text-primary-700 shadow-sm';

export function BongHoi({ children }: { children: ReactNode }) {
  return (
    <div className="flex justify-end">
      <p className={BONG_HOI}>{children}</p>
    </div>
  );
}

/** Nhãn mục — tách NỘI DUNG khỏi CĂN CỨ bằng mắt, không bằng một câu giải thích. */
function Muc({ ten, open, onToggle, children }: { ten: string; open: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <section className="pt-1">
      <div className="flex items-center gap-2 border-t border-default pt-2">
        <button
          type="button"
          aria-expanded={open}
          className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-content-muted hover:text-content-primary"
          onClick={onToggle}
        >
          <RightOutlined className={cn('text-xs transition-transform', open && 'rotate-90')} />
          {ten}
        </button>
      </div>
      {open && <div className="mt-2">{children}</div>}
    </section>
  );
}

/** Câu lỗi của một lượt: câu máy chủ nói, hoặc câu của catalog khi lỗi tới trước khi luồng mở mà không mang câu nào. */
function useCauLoi(): (e: AskError) => string {
  const { t } = useT();
  const moTaLoi = useMoTaLoi();
  return (e) => {
    if (e.message) return e.message;
    if (e.code === 'TIMEOUT') return t('troLyAiHoiLuot.hetGio');
    return moTaLoi({ code: e.code, message: '' });
  };
}

/** Lượt "chạm trần thời gian": phần diễn giải bị bỏ — phải vẽ ra, không được để im. */
function QuaTran({ bot }: { bot?: boolean }) {
  const { t } = useT();
  return (
    <p className="flex items-start gap-1.5 rounded bg-warning-50 px-2.5 py-1.5 text-xs text-warning-700">
      <span aria-hidden="true">⏱</span>
      <span>
        <b>{t('troLyAiHoiLuot.quaTranDam')}</b> {t(bot ? 'troLyAiHoiLuot.quaTranBotMo' : 'troLyAiHoiLuot.quaTranMo')}
      </span>
    </p>
  );
}

/**
 * Lời văn theo loại câu hỏi đã nhận dạng: câu HỎI LẠI vẽ khác đi — nếu giống câu trả lời thì người đọc bỏ qua phần lựa
 * chọn bên dưới, mà phần ấy mới là chỗ đi tiếp được.
 */
function LoiVanTheoLoai({ r }: { r: AskResult }) {
  const { t } = useT();
  if (r.kind === 'clarify') {
    return (
      <div className="rounded-lg border border-primary-200 bg-primary-50 p-3.5">
        <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-primary-700">
          <QuestionCircleOutlined className="text-xs" />
          {t('troLyAiHoiLuot.canLamRo')}
        </p>
        <LoiVan text={r.answer} className="text-sm leading-relaxed text-content-primary" />
      </div>
    );
  }
  if (r.kind === 'unsupported') {
    return (
      <div className="rounded-lg border border-default bg-subtle p-3.5">
        <LoiVan text={r.answer} className="text-sm leading-relaxed text-content-secondary" />
      </div>
    );
  }
  return <LoiVan text={r.answer} className="text-sm leading-relaxed text-content-primary" />;
}

function DongSoLieu({ rowCount, truncated, freshness, syncedAt, elapsedMs }: {
  rowCount: number;
  truncated: boolean;
  freshness?: string | null;
  syncedAt?: string | null;
  elapsedMs?: number;
}) {
  const { t, locale } = useT();
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-content-muted">
      <span>{t('troLyAiHoiLuot.dong', { n: soVN(rowCount, locale) })}</span>
      {truncated && <Badge variant="warning">{t('troLyAiHoiLuot.daCatBot')}</Badge>}
      {/* Ba thứ đi cùng nhau hoặc không đi: câu lệnh, số dòng, tuổi số liệu. */}
      <span>
        {freshness === 'live'
          ? t('troLyAiHoiLuot.soLieuTrucTiep')
          : t('troLyAiHoiLuot.soLieuToi', { luc: syncedAt ? ngayGioVN(syncedAt) : '—' })}
      </span>
      {elapsedMs !== undefined && <span>· {(elapsedMs / 1000).toLocaleString(locale, { maximumFractionDigits: 1 })} s</span>}
    </div>
  );
}

/**
 * Một lượt của Hỏi đáp AI.
 *
 * Không có bảng số liệu thô và câu SQL ở đây: "câu lệnh nào đã chạy" là việc truy vết, có chỗ riêng đầy đủ hơn ở màn
 * Nhật ký truy vết — hai chỗ cùng hiện một thứ là hai chỗ sẽ trôi khỏi nhau. ⚠️ Đánh đổi đã biết (như native): mất "Chạy
 * lại câu đã sửa" và "Lưu làm câu mẫu" — màn Thử nghiệm làm được việc thứ nhất.
 */
export function LuotHoi({
  turn,
  sourceId,
  onHoiTiep,
}: {
  turn: Turn;
  /** Nguồn đang hỏi — đi vào đường dẫn sang màn khai báo, để người bấm hạ cánh đúng nguồn. */
  sourceId?: string;
  onHoiTiep: (question: string) => void;
}) {
  const { t, locale } = useT();
  const duongDan = useDuongDan();
  const cauLoi = useCauLoi();
  const [openDocs, setOpenDocs] = useState(false);
  const r = turn.result;
  // Hai nguồn trở lên thì hàng số liệu chung (khung nhìn của nguồn ĐẦU TIÊN) thành lời nói dối — mỗi nguồn một khối.
  const daNguon = (r?.nguon?.length ?? 0) > 1;

  return (
    <article className="space-y-2">
      <BongHoi>{turn.question}</BongHoi>

      <TienTrinh
        steps={turn.steps}
        running={turn.running}
        attempts={r?.attempts ?? 1}
        khongNguon={r?.unsourcedNumbers ?? []}
        cauVuotBang={r?.partialViewClaims ?? []}
        nodes={r?.nodes}
        soLanGoiLlm={r?.soLanGoiLlm}
        model={r?.tenCongKhai}
      />

      {turn.error && (
        <section role="alert" className="rounded-lg border border-danger-500 bg-danger-50 p-3.5">
          <h3 className="mb-2 text-xs font-semibold text-danger-700">{t('troLyAiHoiLuot.khongTraLoiDuoc')}</h3>
          <p className="break-words text-xs leading-relaxed text-danger-700">{cauLoi(turn.error)}</p>
          {/* Lời từ chối là chữ của bộ canh cổng — thu gọn để câu trả lời không mang lỗi kỹ thuật, nhưng quản trị vẫn
              mở được để khai đúng cột. */}
          {turn.error.refusals?.length ? (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-danger-700">{t('troLyAiHoiLuot.chiTietKyThuat')}</summary>
              <ul className="mt-1.5 space-y-1.5">
                {turn.error.refusals.map((x, i) => (
                  <li key={i} className="break-words text-xs leading-relaxed text-danger-700">
                    <span className="font-mono text-xs">{x.code}</span> — {x.message}
                    {x.bang && x.cot ? (
                      <Link
                        to={duongDan(duongKhaiCot({ bang: x.bang, cot: x.cot, nguon: turn.error?.nguon || sourceId }))}
                        className="ms-1.5 whitespace-nowrap font-medium underline underline-offset-2"
                      >
                        {t('troLyAiHoiLuot.khaiCotNay', { cot: x.cot })}
                      </Link>
                    ) : null}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          {turn.error.followUps?.length ? (
            <CauHoiTiep ds={turn.error.followUps} nhan={t('troLyAiHoiLuot.thuHoi')} onChon={onHoiTiep} />
          ) : null}
        </section>
      )}

      {/* LỜI VĂN ĐANG PHÁT — ngoài khối `r &&`: `result` chỉ đặt lúc `done`. Sau `done` thì `r.answer` thắng — nó đã
          qua kiểm số, bản phát dần là chữ thô. */}
      {!r?.answer && turn.vanDang ? (
        <LoiVan text={turn.vanDang} className="text-sm leading-relaxed text-content-primary" />
      ) : null}

      {r && (
        <>
          {/* Kịch bản đứng TRƯỚC lời văn: nó là khung cảnh để đọc câu tiếp theo. */}
          {r.kichBan ? <TienDoKichBan kichBan={r.kichBan} /> : null}
          {r.quaTran ? <QuaTran /> : null}
          {r.answer ? <LoiVanTheoLoai r={r} /> : null}

          {/* Hàng số liệu chỉ có nghĩa khi có truy vấn — với câu chào, "0 dòng · số liệu tới —" nói rằng có gì vừa hỏng. */}
          {r.sql && !daNguon ? (
            <DongSoLieu
              rowCount={r.rowCount}
              truncated={r.truncated}
              freshness={r.freshness}
              syncedAt={r.syncedAt}
              elapsedMs={r.elapsedMs}
            />
          ) : null}

          {r.warnings.map((w, i) => (
            <p key={i} className="text-xs text-warning-700">
              ⚠ {w}
            </p>
          ))}

          {/* KHÔNG vẽ `anomalies` (so ngoại lai trên cột MÃ SỐ, vô nghĩa). Chỉ cảnh báo viết cho NGƯỜI HỎI — loại
              `rieng` là chuyện của người vận hành, vẫn còn ở thanh tiến trình và màn Truy vết. */}
          {r.checks?.some((x) => !x.rieng) ? (
            <ul className="space-y-1">
              {r.checks
                .filter((x) => !x.rieng)
                .map((x, i) => (
                  <li key={i} className="text-xs leading-relaxed text-content-secondary">
                    ↕ {x.message}
                  </li>
                ))}
            </ul>
          ) : null}

          {/* Ba tầng số: tầng 1 đã bị chặn; tầng 3 (`labelled`) đã nằm sẵn trong lời văn — vẽ nữa là in cùng câu hai lần.
              Chỉ tầng 2 ở đây: "tăng 8%" không nói tính từ đâu là câu không kiểm lại được. */}
          {r.derivedNumbers?.length ? (
            <section className="rounded-lg border border-default p-3.5">
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-content-muted">
                {t('troLyAiHoiLuot.nguonGocSo')}
              </p>
              <p className="text-xs leading-relaxed text-content-secondary">
                <Badge variant="neutral">{t('troLyAiHoiLuot.soTinhRa')}</Badge> {r.derivedNumbers.join(' · ')}
              </p>
            </section>
          ) : null}

          {/* Viền và nhãn riêng: phần duy nhất không quy về được một ô hay một đoạn tài liệu — trình bày giống câu trả
              lời thì người đọc mang cả hai đi họp với cùng một mức tin cậy. */}
          {r.suggestions?.length ? (
            <section className="rounded-lg border border-warning-500 bg-warning-50 p-3.5">
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-warning-700">
                <WarningOutlined className="text-xs" />
                {t('troLyAiHoiLuot.deXuat')}
              </p>
              <ul className="space-y-1">
                {r.suggestions.map((x, i) => (
                  <li key={i} className="text-xs leading-relaxed text-content-secondary">
                    · {x}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {r.followUps?.length ? (
            <CauHoiTiep
              ds={r.followUps}
              nhan={
                r.kind === 'clarify'
                  ? t('troLyAiHoiLuot.chonMotCauCuThe')
                  : r.kind === 'greeting' || r.kind === 'capability'
                    ? t('troLyAiHoiLuot.thuHoi')
                    : t('troLyAiHoiLuot.hoiTiep')
              }
              onChon={onHoiTiep}
            />
          ) : null}

          {r.passages?.length ? (
            <Muc
              ten={t('troLyAiHoiLuot.taiLieuDaDan', { n: soVN(r.passages.length, locale) })}
              open={openDocs}
              onToggle={() => setOpenDocs((v) => !v)}
            >
              <div className="space-y-2">
                {r.passages.map((p, i) => (
                  <div key={i} className="rounded-lg border border-default p-3">
                    {/* Đoạn web chỉ kiểm được bằng ĐỊA CHỈ, và không in `score` — điểm của đoạn web suy từ hạng, không
                        đo được, đặt cạnh điểm đo được là mời so hai thứ khác thang. */}
                    <p className="mb-1 break-words text-xs text-content-muted">
                      {p.fileName} · {p.collectionName}
                      {p.nguon === 'web' ? (
                        p.url ? (
                          <>
                            {' · '}
                            <a
                              href={p.url}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="underline underline-offset-2 hover:text-content-primary"
                            >
                              {p.url}
                            </a>
                          </>
                        ) : null
                      ) : (
                        <>
                          {' · '}
                          {t('troLyAiHoiLuot.doan', { n: soVN(p.chunkIndex + 1, locale) })} ·{' '}
                          {p.score.toLocaleString(locale, { maximumFractionDigits: 3, minimumFractionDigits: 3 })}
                        </>
                      )}
                    </p>
                    <p className="whitespace-pre-wrap text-xs leading-relaxed text-content-secondary">{p.text}</p>
                  </div>
                ))}
              </div>
            </Muc>
          ) : null}

          {/* MỘT KHỐI MỖI NGUỒN: mỗi nguồn là một CSDL riêng, hai bảng không ghép được thành một; mốc đồng bộ đi theo
              từng khối. Biểu đồ chỉ vẽ khi được yêu cầu (`display.theoYeuCau`) — xem `KhoiSoLieu`. */}
          {daNguon ? (
            <div className="space-y-4">
              {r.nguon?.map((n) => (
                <div key={n.sourceId} className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                    <Badge variant="neutral">{n.ten}</Badge>
                    {n.loi ? (
                      <span className="text-warning-700">{n.loi}</span>
                    ) : (
                      <DongSoLieu rowCount={n.rowCount} truncated={n.truncated} freshness={n.freshness} syncedAt={n.syncedAt} />
                    )}
                  </div>
                  {n.loi ? null : (
                    <KhoiSoLieu
                      rows={n.rows}
                      columns={n.columns}
                      nhanCot={n.nhanCot}
                      display={r.display}
                      veBang={r.veBang}
                      hoiDanhSach={r.hoiDanhSach}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <KhoiSoLieu
              rows={r.rows}
              columns={r.columns}
              nhanCot={r.nhanCot}
              display={r.display}
              veBang={r.veBang}
              hoiDanhSach={r.hoiDanhSach}
            />
          )}

          <NguonBoQua r={r} />
        </>
      )}
    </article>
  );
}

/**
 * Nguồn đã gắn mà lượt này KHÔNG hỏi. Từ khi bộ lọc theo từ vựng bỏ nguồn TỰ ĐỘNG, im lặng ở đây thành một câu trả lời
 * về hai trong năm nguồn trông y hệt một câu trả lời về tất cả. Vẽ MỜ và gập lại — không phải lỗi — nhưng ngay dưới bảng.
 */
function NguonBoQua({ r }: { r: AskResult }) {
  const { t, locale } = useT();
  const ds = r.nguonBoQua ?? [];
  if (!ds.length) return null;
  return (
    <details className="mt-3 text-xs text-content-muted">
      <summary className="cursor-pointer">
        <span className="underline decoration-dotted underline-offset-2">
          {t('troLyAiHoiLuot.khongTraONguon', { n: soVN(ds.length, locale) })}
        </span>
        {': '}
        {ds.map((n) => n.ten).join(' · ')}
      </summary>
      <ul className="mt-1.5 list-disc space-y-0.5 ps-4">
        {ds.map((n) => (
          <li key={n.sourceId}>
            <span className="text-content-secondary">{n.ten}</span>
            {' — '}
            {n.vi_sao}
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * Một lượt của màn chat với bot — gọn hơn lượt của Hỏi đáp AI (không mục tài liệu, không nguồn gốc số), thêm chế độ
 * chỉ-giọng-nói.
 *
 * Ẩn chữ CÓ ĐIỀU KIỆN ở kênh `giong_noi`: máy chủ cố ý không ném khi tổng hợp một câu hỏng (câu ấy biến khỏi bản đọc,
 * im lặng). Ẩn vô điều kiện thì đúng lúc ấy người dùng nhận TRỐNG KHÔNG. Chặng "đọc thành tiếng" báo `warn` thì hiện chữ
 * lại và nói vì sao.
 */
export function LuotChatBot({
  turn,
  onHoiTiep,
  chiGiongNoi,
}: {
  turn: Turn;
  onHoiTiep: (q: string) => void;
  chiGiongNoi: boolean;
}) {
  const { t } = useT();
  const cauLoi = useCauLoi();
  const r = turn.result;
  const changDoc = turn.steps.find((s) => s.step === CHANG_DOC_THANH_TIENG);
  const docHong = changDoc?.status === 'warn';
  const dangDoc = chiGiongNoi && turn.running;
  const anChu = chiGiongNoi && !docHong && (turn.running || changDoc?.status === 'ok');

  return (
    <div className="space-y-2">
      <BongHoi>{turn.question}</BongHoi>

      <TienTrinh
        steps={turn.steps}
        running={turn.running}
        attempts={r?.attempts ?? 1}
        khongNguon={r?.unsourcedNumbers ?? []}
        cauVuotBang={r?.partialViewClaims ?? []}
        nodes={r?.nodes}
        soLanGoiLlm={r?.soLanGoiLlm}
        model={r?.tenCongKhai}
      />

      {turn.error ? (
        <div role="alert" className="rounded-lg border border-danger-500 bg-danger-50 p-3.5">
          <p className="break-words text-xs leading-relaxed text-danger-700">{cauLoi(turn.error)}</p>
        </div>
      ) : null}

      {r?.kichBan ? <TienDoKichBan kichBan={r.kichBan} /> : null}
      {r?.quaTran ? <QuaTran bot /> : null}

      {anChu ? (
        <p className="flex items-start gap-1.5 rounded bg-subtle px-2.5 py-1.5 text-xs text-content-secondary">
          <span aria-hidden="true">{dangDoc ? '🔊' : '🎧'}</span>
          <span>{dangDoc ? t('troLyAiHoiLuot.dangDocThanhTieng') : t('troLyAiHoiLuot.chuAnViGiongNoi')}</span>
        </p>
      ) : null}

      {docHong ? (
        <p className="flex items-start gap-1.5 rounded bg-warning-50 px-2.5 py-1.5 text-xs text-warning-700">
          <span aria-hidden="true">⚠</span>
          <span>{t('troLyAiHoiLuot.docHongHienChu')}</span>
        </p>
      ) : null}

      {anChu ? null : r?.answer ? (
        <LoiVanTheoLoai r={r} />
      ) : turn.vanDang ? (
        <LoiVan text={turn.vanDang} className="text-sm leading-relaxed text-content-primary" />
      ) : null}

      {/* Chế độ chỉ-giọng-nói cũng không hiện bảng — một lưới 13 cột hiện ra khi người dùng đang NGHE là thứ họ không
          xin. `KhoiSoLieu` (không phải bảng trần) để công tắc "Biểu đồ · Luôn vẽ" có tác dụng ở màn này. */}
      {!anChu ? (
        <KhoiSoLieu
          rows={r?.rows}
          columns={r?.columns}
          nhanCot={r?.nhanCot}
          display={r?.display}
          veBang={r?.veBang}
          hoiDanhSach={r?.hoiDanhSach}
        />
      ) : null}

      {r?.warnings?.map((w, i) => (
        <p key={i} className="text-xs text-warning-700">
          ⚠ {w}
        </p>
      ))}

      {r?.followUps?.length ? <CauHoiTiep ds={r.followUps} nhan={t('troLyAiHoiLuot.hoiTiep')} onChon={onHoiTiep} /> : null}
    </div>
  );
}
